// Admin console sub-router. Mounted from App.tsx as
// `<Route path="/admin/*" element={<AdminApp />} />`. The admin tree
// has its own auth context (AdminAuthProvider) and layout
// (AdminLayout); the marketing/dashboard chrome is intentionally NOT
// rendered.

import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminAuthProvider } from './adminAuth';
import { AdminLayout } from './components/AdminLayout';
import { RequireAdmin } from './components/RequireAdmin';
import { Admin2FASetup } from './pages/Admin2FASetup';
import { Admin2FAVerify } from './pages/Admin2FAVerify';
import { AdminBoatNotebook } from './pages/AdminBoatNotebook';
import { AdminBoats } from './pages/AdminBoats';
import { AdminDeviceDetail } from './pages/AdminDeviceDetail';
import { AdminDevices } from './pages/AdminDevices';
import { AdminEvalAgreement } from './pages/AdminEvalAgreement';
import { AdminEvalCaseDraft } from './pages/AdminEvalCaseDraft';
import { AdminEvalCases } from './pages/AdminEvalCases';
import { AdminEvalCompare } from './pages/AdminEvalCompare';
import { AdminEvalConsents } from './pages/AdminEvalConsents';
import { AdminEvalGuide } from './pages/AdminEvalGuide';
import { AdminEvalLabelItem } from './pages/AdminEvalLabelItem';
import { AdminEvalLabels } from './pages/AdminEvalLabels';
import { AdminEvalLabelSet } from './pages/AdminEvalLabelSet';
import { AdminEvalLaunch } from './pages/AdminEvalLaunch';
import { AdminEvalRunDetail } from './pages/AdminEvalRunDetail';
import { AdminEvalRuns } from './pages/AdminEvalRuns';
import { AdminEvalsOverview } from './pages/AdminEvalsOverview';
import { AdminIngestUsage } from './pages/AdminIngestUsage';
import { AdminLlmUsage } from './pages/AdminLlmUsage';
import { AdminLogin } from './pages/AdminLogin';
import { AdminManuals } from './pages/AdminManuals';
import { AdminBetaSignups } from './pages/AdminBetaSignups';
import { AdminStatus } from './pages/AdminStatus';
import { AdminModels } from './pages/AdminModels';
import { AdminNeoChangeDetail } from './pages/AdminNeoChangeDetail';
import { AdminNeoChanges } from './pages/AdminNeoChanges';
import { AdminNeoGate } from './pages/AdminNeoGate';
import { AdminNeoPageRefusalDetail } from './pages/AdminNeoPageRefusalDetail';
import { AdminNeoPageRefusals } from './pages/AdminNeoPageRefusals';
import { AdminNeoRefusals } from './pages/AdminNeoRefusals';
import { AdminNeoSessionDetail } from './pages/AdminNeoSessionDetail';
import { AdminNeoSessions } from './pages/AdminNeoSessions';
import { AdminRejectedPaths } from './pages/AdminRejectedPaths';
import { AdminSearchUsage } from './pages/AdminSearchUsage';
import { AdminUserDetail } from './pages/AdminUserDetail';
import { AdminUsers } from './pages/AdminUsers';
// The Evals section's styles (task eval_admin_runs), class-scoped to .eval-*.
import '../styles/admin-evals.css';
// The eval case inbox's pages (TOM-140), class-scoped to .eval-inbox*.
import '../styles/admin-eval-cases.css';
// The Evals guide and each page's "How this page works" link (TOM-139).
import '../styles/admin-eval-guide.css';
// A boat's notebook (TOM-616), class-scoped to .notebook-*.
import '../styles/admin-boat-notebook.css';

export function AdminApp() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route path="setup-2fa" element={<Admin2FASetup />} />
        <Route path="verify-2fa" element={<Admin2FAVerify />} />
        <Route
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<AdminUsers />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="boats" element={<AdminBoats />} />
          {/* A boat's notebook, read with a reason (TOM-616). */}
          <Route path="boats/:boatId/notebook" element={<AdminBoatNotebook />} />
          <Route path="devices" element={<AdminDevices />} />
          <Route path="devices/:id" element={<AdminDeviceDetail />} />
          <Route path="manuals" element={<AdminManuals />} />
          <Route path="models" element={<AdminModels />} />
          <Route path="beta-signups" element={<AdminBetaSignups />} />
          <Route path="status" element={<AdminStatus />} />
          <Route path="neo" element={<AdminNeoSessions />} />
          <Route path="neo/sessions/:id" element={<AdminNeoSessionDetail />} />
          <Route path="neo/refusals" element={<AdminNeoRefusals />} />
          {/* Refused board page saves, read with a reason (TOM-672). */}
          <Route path="neo/page-refusals" element={<AdminNeoPageRefusals />} />
          <Route path="neo/page-refusals/:id" element={<AdminNeoPageRefusalDetail />} />
          <Route path="neo/gate" element={<AdminNeoGate />} />
          <Route path="neo/changes" element={<AdminNeoChanges />} />
          <Route path="neo/changes/:id" element={<AdminNeoChangeDetail />} />
          {/* Evals (task eval_admin_runs): the overview, every stored run, one
              run with its cases, and a comparison of two runs. */}
          <Route path="evals" element={<AdminEvalsOverview />} />
          <Route path="evals/runs" element={<AdminEvalRuns />} />
          <Route path="evals/runs/:id" element={<AdminEvalRunDetail />} />
          <Route path="evals/compare" element={<AdminEvalCompare />} />
          {/* The eval case inbox (TOM-140): the queue, one candidate's draft,
              and the recorded-moment consents, reached from the queue. */}
          <Route path="evals/cases" element={<AdminEvalCases />} />
          <Route path="evals/cases/consents" element={<AdminEvalConsents />} />
          <Route path="evals/cases/:ref" element={<AdminEvalCaseDraft />} />
          {/* Judge calibration (TOM-141): the sets, one version, blind
              labelling of one item, and the agreement report. */}
          <Route path="evals/labels" element={<AdminEvalLabels />} />
          <Route path="evals/labels/:setId" element={<AdminEvalLabelSet />} />
          <Route path="evals/labels/:setId/items/:itemId" element={<AdminEvalLabelItem />} />
          <Route path="evals/labels/:setId/report" element={<AdminEvalAgreement />} />
          {/* Launch a run on the worker (TOM-120, ADR 0248). */}
          <Route path="evals/launch" element={<AdminEvalLaunch />} />
          {/* How the section works, and how to improve Neo and its experts
              with the console (TOM-139). */}
          <Route path="evals/guide" element={<AdminEvalGuide />} />
          <Route path="usage/llm" element={<AdminLlmUsage />} />
          <Route path="usage/search" element={<AdminSearchUsage />} />
          <Route path="usage/rejected-telemetry" element={<AdminRejectedPaths />} />
          <Route path="usage/ingest" element={<AdminIngestUsage />} />
          {/* Redirects from the pre-ADR-0076 paths so existing bookmarks /
              audit deep links survive the IA move. */}
          <Route path="manuals/review" element={<Navigate to="/admin/manuals" replace />} />
          <Route path="llm-usage" element={<Navigate to="/admin/usage/llm" replace />} />
        </Route>
      </Routes>
    </AdminAuthProvider>
  );
}
